import React from 'react'
import PropTypes from 'prop-types'

import { addCssUrlToHead, addCssToHead, addScriptToHead } from '../../utils/DocumentHelper'
import { distanceBetweenTwoLatLngsInPx } from './LeafletTools'
import LeafletControlInterface from './LeafletControlInterface'
import { mapLangToAvailableLangForTiles } from './Languages'
import { SCRIPT_URLS, CSS_URLS } from './Urls'
import SimpleCache from '../../utils/SimpleCache'
import ReactDOMServer from 'react-dom/server'

export const PROVIDERS = {
  HERE: 'HERE',
  OSM: 'OSM'
}

export const BOUNDS_EUROPE = [{lat: 64.2790, lng: -14.9143}, {lat: 36.90087, lng: 30.69371}]

const MIN_INITIAL_ZOOM_LEVEL = 3
const USER_POSITION_ZOOM_LEVEL = 13
const MIN_PAN_DISTANCE = 100   // in px

const ID_PREFIXES = {
  SCRIPT: 'LMS_',  // stands for Leaflet Maps Script
  CSS: 'LMC_'      // stands for Leaflet Maps Css
}

const EXTRA_CSS_KEY = 'EXTRA_CSS'

let GLOBAL_MAP_COUNTER = 0

class Leaflet extends React.Component {

  /** @namespace window.H.* */

  constructor (props) {
    super(props)

    GLOBAL_MAP_COUNTER += 1

    this.currentMarkers = {}

    this.userPositionAvailable = true   // optimistic approach: enable positioning button until we know we don't have the position
    this.userPositionLatLng = null
    this.userPositionBounds = null
    this.userPositionAccuracy = null
    this.userPositionMarker = null
    this.locationMarker = null

    this.positionPromiseResolve = null
    this.positionPromiseReject = null

    this.outerDivId = Math.random().toString(36).slice(2, 5)
    this.map = null // will be set in initMap
    this.ui = null  // will be set in initMap
    this.markerClusterGroup = null // will be set in initMap

    this.previousZoomLevel = 0

    this.simpleCache = new SimpleCache()

    this.hasInitialUserPosition = !props.initiallyGoToUserPosition

    this.isUpdatingMarkers = false
    this.disableNextMapMoveEvent = false
    this.disabledPanningForPoisWithLatLng = []
    this.disablePopupClose = false
    this.isPanning = false
    this.panDoneCallbacks = []

    this.popupOpenForPoi = null
    this.cancelBoundsChanged = null

    this.clusterOpenDate = 0
  }

  componentWillMount () {
    this.loadAndInit(this.props)
  }

  componentWillReceiveProps (nextProps) {
    // it may be that the map is not yet loaded --> really?
    // if (this.map) {

    if (Date.now() - this.clusterOpenDate < 5000) {
      return
    }

    const afterClosing = () => {
      if (this.props.bounds !== nextProps.bounds) {
        this.map.fitBounds(nextProps.bounds)  // animated
      }
      // }

      this.setPois(nextProps.pois)
    }

    if (this.popupOpenForPoi) {
      // if there is an open popup but the corresponding POI is not among the next POIs we need to close that popup
      const openedPoiInNextPois = nextProps.pois.find(poi => poi.id === this.popupOpenForPoi)
      // console.log('openedPoiInNextPois', openedPoiInNextPois)
      if (!openedPoiInNextPois || !openedPoiInNextPois.isVisible) {
        this.map.closePopup()

        setTimeout(() => afterClosing(), 1000)
        return
      }
    }

    if (this.props.locationMarkerPosition !== nextProps.locationMarkerPosition) {
      this.setLocationMarker(nextProps)
    }

    afterClosing()
  }

  componentWillUnmount () {
    this.map.stopLocate()

    GLOBAL_MAP_COUNTER -= 1
    if (GLOBAL_MAP_COUNTER <= 0) {
      this.unloadScriptsAndStyles()
    }
  }

  addPanEndCallback (callback) {
    if (!this.isPanning) {
      callback()
    }
    else {
      this.panDoneCallbacks.push(callback)
    }
  }

  centerAroundPois = (pois) => {
    const bounds = pois.map(poi => {
      return [poi.getPosition().lat, poi.getPosition().lng]
    })
    if (bounds.length === 0) {
      return
    }
    this.map.fitBounds(bounds)
  }

  getCssId (key) {
    return `${ID_PREFIXES.CSS}${key}`
  }

  getScriptId (key) {
    return `${ID_PREFIXES.SCRIPT}${key}`
  }

  getHighlightedIconForPoi (poi) {
    if (!poi) {
      return null
    }
    let iconHighlighted = this.simpleCache.getIcon(`${poi.category + poi.status}_highlighted`)
    if (!iconHighlighted) {
      iconHighlighted = window.L.icon({
        iconUrl: poi.getMarkerHighlightedIcon(this.props.functions),

        // TODO: put these settings to the global json config
        iconSize: [40, 40], // size of the icon
        iconAnchor: [20, 40], // point of the icon which will correspond to marker's location
        popupAnchor: [0, -42] // point from which the popup should open relative to the iconAnchor
      })
      this.simpleCache.setIcon((`${poi.category}_highlighted`), iconHighlighted)
    }
    return iconHighlighted
  }

  getStandardIconForPoi (poi) {
    if (!poi) {
      return null
    }
    let icon = this.simpleCache.getIcon(poi.category + poi.status)
    if (!icon) {
      icon = window.L.icon({
        iconUrl: poi.getMarkerIcon(this.props.functions),

        // TODO: put these settings to the global json config
        iconSize: [40, 40], // size of the icon
        iconAnchor: [20, 40], // point of the icon which will correspond to marker's location
        popupAnchor: [0, -42] // point from which the popup should open relative to the iconAnchor
      })
      this.simpleCache.setIcon(poi.category, icon)
    }
    return icon
  }

  getUserPosition = () => {
    // return this.userPositionLatLng
    return this.props.defaultUserPosition
  }

  hasUserPosition = () => {
    return this.userPositionAvailable
  }

  initMap (props) {
    const bounds = props.initialBounds.map(bound => window.L.latLng(bound.lat, bound.lng))

    this.map = window.L.map(this.outerDivId, {
      zoomControl: false,
      zoomDelta: 1,  // zoom change on double tap
      zoomSnap: window.L.Browser.mobile ? 0.25 : 1, // zoom snap on pinch or scroll
      maxBounds: window.L.latLngBounds(window.L.latLng(-90, -180), window.L.latLng(90, 180))
    }).fitBounds(bounds)

    if (this.map.getZoom() < MIN_INITIAL_ZOOM_LEVEL) {
      this.map.setZoom(MIN_INITIAL_ZOOM_LEVEL)
    }

    let url
    let options

    if (props.provider === PROVIDERS.HERE) {
      const year = new Date().getFullYear()

      let attribution
      if (window.KesiTools) {
        attribution = `Map &copy; ${year} <a onclick="window.KesiTools.openWebsiteInSystemBrowser('http://developer.here.com')">HERE</a> ${this.props.dataSource}`
      }
      else {
        attribution = `Map &copy; ${year} <a href="http://developer.here.com">HERE</a> ${this.props.dataSource}`
      }

      const cit = props.useCIT ? '.cit' : ''
      url = `https://{s}.{base}.maps${cit}.api.here.com/maptile/2.1/{type}/{mapID}/{scheme}/{z}/{x}/{y}/{size}/{format}?app_id={app_id}&app_code={app_code}&lg={language}&ppi={ppi}`
      options = {  // here maps
        attribution,
        subdomains: '1234',
        base: 'base',
        type: 'maptile',
        scheme: 'normal.day',
        app_id: props.appId,
        app_code: props.appCode,
        mapID: 'newest',
        maxZoom: 17,
        language: mapLangToAvailableLangForTiles(props.lang),
        size: window.L.Browser.retina ? 512 : 256,
        tileSize: 256,
        ppi: window.L.Browser.retina ? 250 : 72,
        format: 'png8'
      }
    }
    else if (props.provider === PROVIDERS.OSM) {
      url = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      options = {
        attribution: 'Map data © <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
      }
    }
    else {
      console.error(`Given map tile provider "${props.provider}" is not supported. Please choose one of ${JSON.stringify(Object.values(PROVIDERS))}.`)
      return
    }

    window.L.tileLayer(url, options).addTo(this.map)

    this.markerClusterGroup = window.L.markerClusterGroup({
      spiderfyDistanceMultiplier: 1.5
    })
    this.markerClusterGroup.on('clusterclick', this.onClusterClick)
    this.markerClusterGroup.on('animationend', this.onAnimationEnd)
    this.markerClusterGroup.on('spiderfied', this.onSpiderfy)
    this.markerClusterGroup.on('unspiderfied', this.onUnspiderfy)
    this.map.addLayer(this.markerClusterGroup)

    this.setUserPositionMarker()
    this.setLocationMarker(this.props)

    // this.map.on('click', this.onMapClick)
    this.map.on('moveend', this.onBoundsChanged)
    // this.map.on('preclick', this.onPreClick)
    // this.map.on('mousedown', this.onPreClick)
    this.map.on('zoomend', this.onZoomChanged)
    this.map.on('locationfound', this.onLocationFound)
    this.map.on('locationerror', this.onLocationError)

    if (props.controllerInterfaceRegistration) {
      const controller = new LeafletControlInterface(
        this.positionMapAtUserPosition,
        this.positionMapAtLocation,
        this.hasUserPosition,
        this.centerAroundPois,
        this.showPopup,
        this.getUserPosition,
      )
      props.controllerInterfaceRegistration(controller)
    }
    this.positionMapAtUserPosition(true)
  }

  async loadAllNeededScripts (props) {
    // core is always needed and must be loaded first
    await Promise.all([
      addCssUrlToHead(CSS_URLS.CORE, this.getCssId('CORE')),
      addCssUrlToHead(CSS_URLS.CLUSTER, this.getCssId('CLUSTER')),
      addCssUrlToHead(CSS_URLS.CLUSTER_DEFAULT, this.getCssId('CLUSTER_DEFAULT')),

      // use same color for clusters indepentent from size
      // yellow
      addCssToHead('.marker-cluster { background-color: rgba(241, 211, 87, 0.6) } .marker-cluster div { background-color: rgba(240, 194, 12, 0.6) }', this.getCssId('CUSTOM_MARKER_CLUSTER_CSS'))
    ])
    await addScriptToHead(SCRIPT_URLS.CORE, this.getScriptId('CORE'))
    await addScriptToHead(SCRIPT_URLS.CLUSTER, this.getScriptId('CLUSTER'))

    return Promise.resolve()
  }

  async loadAndInit (props) {
    await this.loadAllNeededScripts(this.props)
    this.initMap(props)
  }

  /**
   * @param poi {Poi}
   * @returns {boolean}
   */
  isPoiWithDisabledPanning (poi) {
    return this.disabledPanningForPoisWithLatLng.some(latlng => {
      return latlng.lat === poi.lat && latlng.lng === poi.lng
    })
  }

  onAnimationEnd = (evt) => {
    // debugger
  }

  onBoundsChanged = (evt) => {
    if (this.disableNextMapMoveEvent) {
      // setTimeout(() => {
      //   this.disableNextMapMoveEvent = false
      // }, 300)
      return
    }
    // console.log('onBoundsChanged not disabled')

    if (this.cancelBoundsChanged) {
      this.cancelBoundsChanged()
    }

    const centerPoint = this.map.getCenter()
    const viewBounds = this.map.getBounds()
    const viewSize = this.map.getSize()
    const nextZoom = this.map.getZoom()

    // noinspection JSSuspiciousNameCombination
    const mapValues = {
      zoom: nextZoom,
      center: {lat: centerPoint.lat, lng: centerPoint.lng},
      size: {width: viewSize.y, height: viewSize.x},
      marginBounds: {
        nw: {lat: viewBounds.getNorthWest().lat, lng: viewBounds.getNorthWest().lng},
        se: {lat: viewBounds.getSouthEast().lat, lng: viewBounds.getSouthEast().lng},
      }
    }

    this.map.closePopup()
    this.cancelBoundsChanged = this.props.onBoundsChanged(mapValues)
  }

  onBubbleTapped = (evt) => {
    this.disablePopupClose = true
    // find data attribute in one of the parents
    let element = evt.target
    let data
    while (element && !(data = element.getAttribute('data'))) {
      element = element.parentNode
    }
    this.props.onBubbleTapped(data)
  }

  onClusterClick = (evt) => {
    // save all clustered markers' positions
    this.disabledPanningForPoisWithLatLng = this.disabledPanningForPoisWithLatLng.concat(evt.layer.getAllChildMarkers().map(marker => marker.getLatLng()))
    this.clusterOpenDate = Date.now()
  }

  onLocationFound = (locationEvt) => {
    this.userPositionAvailable = true
    this.userPositionLatLng = locationEvt.latlng
    // this.userPositionAccuracy = locationEvt.accuracy
    // this.userPositionBounds = locationEvt.bounds
    this.setUserPositionMarker()
    if (!this.hasInitialUserPosition && this.props.initiallyGoToUserPosition) {
      this.positionMapAtUserPosition(false)
    }
    this.hasInitialUserPosition = true

    if (this.positionPromiseReject) {
      const fn = this.positionPromiseResolve
      this.positionPromiseReject = null
      this.positionPromiseResolve = null
      fn()
    }
  }

  onLocationError = (errorEvt) => {
    this.userPositionAvailable = false

    const errorMessage = `error retrieving user location: ${errorEvt.code} - ${errorEvt.message}`
    console.log(errorMessage)

    if (this.positionPromiseReject) {
      const fn = this.positionPromiseReject
      this.positionPromiseReject = null
      this.positionPromiseResolve = null
      fn(new Error(errorMessage))
    }
  }

  onMapClick = (evt) => {
  }

  onPreClick = (evt) => {
    // TODO: do this only if no cluster is spiderfied and no tooltip is shown
    // this.panTo(evt.latlng)
  }

  onSpiderfy = (evt) => {

  }

  onUnspiderfy = (evt) => {
    this.disabledPanningForPoisWithLatLng = []
  }

  onZoomChanged = (evt) => {
    if (this.map.getZoom() > this.previousZoomLevel) {
      this.markerClusterGroup.getLayers().forEach(layer => {
        if (!layer.isPopupOpen()) {
          layer.setIcon(this.getStandardIconForPoi(layer.poi))
        }
      })
    }
    this.previousZoomLevel = this.map.getZoom()
    this.disablePopupClose = false
  }

  panTo = async (latlng, options = null) => {
    if (this.isPanning) {
      return
    }
    this.isPanning = true
    let duration = 0
    if (distanceBetweenTwoLatLngsInPx(this.map, this.map.getCenter(), latlng) < MIN_PAN_DISTANCE) {
      duration = 50
    }
    else {
      duration = 300
      if (options && options.animate && options.duration > 0) {
        duration = options.duration * 1000 + 50
      }
      this.map.panTo(latlng, options)
    }

    setTimeout(() => {
      this.isPanning = false
      this.panDoneCallbacks.forEach(callback => callback())
      this.panDoneCallbacks = []
    }, duration)
  }

  positionMapAtLocation = async (position, zoomLevel) => {

    // const icon = L.icon({
    //   iconUrl: this.props.config.user.marker,
    //
    //   iconSize: [40, 40], // size of the icon
    //   iconAnchor: [20, 40], // point of the icon which will correspond to marker's location
    //   popupAnchor: [0, -42] // point from which the popup should open relative to the iconAnchor
    // })
    //
    // this.urlPositionMarker = L.marker(position, {icon})
    // this.map.addLayer(this.urlPositionMarker)
    this.map.setView(position, zoomLevel ? zoomLevel : USER_POSITION_ZOOM_LEVEL, {animate: false})
  }

  positionMapAtUserPosition = async (isInitialPositioning = false) => {
    const userPosition = /*this.userPositionLatLng ||*/ this.props.defaultUserPosition
    this.setUserPositionMarker()
    if (userPosition) {
      if (isInitialPositioning) {
        this.map.setView(userPosition, USER_POSITION_ZOOM_LEVEL, {animate: false})
      }
      else {
        // if the current map position is not too far away from the user location, keep the zoom and center the location
        // "too far" means that it must not be farther away than a certain distance
        // and the current zoom must not be too far away from USER_POSITION_ZOOM_LEVEL

        const distanceInPx = distanceBetweenTwoLatLngsInPx(this.map, userPosition, this.map.getCenter())

        if (distanceInPx > 800 || this.map.getZoom() - USER_POSITION_ZOOM_LEVEL < 0) {
          this.map.setView(userPosition, USER_POSITION_ZOOM_LEVEL)
        }
        else {
          this.panTo(userPosition)
        }
      }
      return Promise.resolve()
    }

    if (window.KesiTools) {
      try {
        const location = await window.KesiTools.getGeolocation()
        this.onLocationFound(location)
        return
      }
      catch (error) {
        console.log('could not get user location from KesiTools', error)
        this.onLocationError(error)
      }
    }

    return new Promise((resolve, reject) => {
      this.positionPromiseResolve = resolve
      this.positionPromiseReject = reject
      // this.map.locate({watch: true})
    })
  }

  removeUserPositionMarker () {
    this.map.removeLayer(this.userPositionMarker)
    this.userPositionMarker = null
  }

  /**
   *
   * @param pois {[Poi]}
   */
  setPois (pois, isSecureCall = false) {
    if (this.disableNextMapMoveEvent || this.isUpdatingMarkers) {
      return
    }
    if (!this.map) {
      return
    }

    if (!isSecureCall) {
      // it may be that new data arrives after tapping a marker but before that open event is called
      // so let's wait a tiny moment just to make sure it's alright
      setTimeout(() => {
        this.setPois(pois, true)
      }, 100)
      return
    }

    // console.log('set pois')

    this.isUpdatingMarkers = true

    // remove those markers that were present before but are not present anymore
    const poiIds = pois.map(poi => poi.id)
    const invisiblePoiIds = pois.filter(poi => !poi.isVisible).map(poi => poi.id)
    const markersToRemove = Object.keys(this.currentMarkers)
      .filter(id => !poiIds.includes(id) || invisiblePoiIds.includes(id))
      .map(id => {
        const marker = this.currentMarkers[id]
        delete this.currentMarkers[id]
        return marker
      })

    this.markerClusterGroup.removeLayers(markersToRemove)
    // this.markerClusterGroup.removeLayers(this.markerClusterGroup.getLayers())

    // check if remaining marker count on layer is what is expected, otherwise clean layer
    // const currentMarkerCount = this.markerClusterGroup.getLayers().length
    // const expectedMarkerCount = Object.keys(this.currentMarkers).length
    // console.log(`(exp: ${expectedMarkerCount}, act: ${currentMarkerCount})`)
    // if(currentMarkerCount !== expectedMarkerCount) {
    //   console.log(`clearing layer because of unexpected marker count! (exp: ${expectedMarkerCount}, act: ${currentMarkerCount})`)
    //   this.markerClusterGroup.clearLayers()
    // }

    const markers = pois
      .filter(poi => !this.currentMarkers[poi.id]) // add only those pois that weren't added yet
      .filter(poi => poi.isVisible)   // add only visible markers
      .map(poi => {
        let icon = this.getStandardIconForPoi(poi)
        let iconHighlighted = this.getHighlightedIconForPoi(poi)

        const popupClassName = `popup_${poi.id}`

        const popup = window.L.popup({
          closeButton: false,
          className: popupClassName
        })

        const marker = window.L.marker([poi.lat, poi.lng], {icon})
        marker.poi = poi

        marker.bindPopup(popup)
          .on('popupopen', (evt) => {
            // console.log('on popup open')
            this.disableNextMapMoveEvent = true

            this.popupOpenForPoi = poi.id

            popup.setContent(`${ReactDOMServer.renderToString(this.props.renderPoiBubbleComponent(poi))}`)

            const poiPoint = this.map.latLngToLayerPoint([poi.lat, poi.lng])
            poiPoint.y -= 60
            if (!this.isPoiWithDisabledPanning(poi)) {
              this.panTo(this.map.layerPointToLatLng(poiPoint))
            }

            marker.setIcon(iconHighlighted)
            marker.openedAtZoomLevel = this.map.getZoom()

            const elements = document.getElementsByClassName(popupClassName)
            if (elements && elements.length > 0) {
              elements[0].setAttribute('data', poi.id)
              elements[0].addEventListener('click', this.onBubbleTapped)
            }

            if (!poi.extraData) {
              this.props.requestExtraData(poi).then((updatedPoi) => {
                // console.log('updated poi', updatedPoi)
                popup.setContent(`${ReactDOMServer.renderToString(this.props.renderPoiBubbleComponent(updatedPoi))}`)
              })
            }
          })
          .on('popupclose', (evt) => {
            // console.log('on popup close', poi)
            setTimeout(() => {
              //   if (this.disablePopupClose) {
              //     return
              //   }
              // this leads to hanging markers ...
              // if (this.map.getZoom() >= marker.openedAtZoomLevel) {
              // marker.setIcon(icon)
              // }
              // ... instead remove this poi and re-add it
              this.markerClusterGroup.removeLayer(marker)
              marker.setIcon(icon)
              this.markerClusterGroup.addLayer(marker)

              // console.log('openpopup: ', this.popupOpenForPoi, 'expected: ', poi.id)
              if (this.popupOpenForPoi === poi.id) {
                this.popupOpenForPoi = null
                this.disableNextMapMoveEvent = false
                this.onBoundsChanged()
                // this.setPois(this.props.pois)
              }
            }, 0)
          })

        this.currentMarkers[poi.id] = marker
        return marker
      })

    this.markerClusterGroup.addLayers(markers)

    this.isUpdatingMarkers = false
  }

  setUserPositionMarker () {
    // don't remove marker if the position was available before

    const userPosition = /*this.userPositionLatLng || */this.props.defaultUserPosition

    // add marker if there wasn't one available before
    if (!this.userPositionMarker && this.props.showUserPosition && userPosition && this.props.config && this.props.config.user && this.props.config.user.marker) {
      const icon = window.L.icon({
        iconUrl: this.props.config.user.marker,

        iconSize: [40, 40], // size of the icon
        iconAnchor: [20, 40], // point of the icon which will correspond to marker's location
        popupAnchor: [0, -42] // point from which the popup should open relative to the iconAnchor
      })

      this.userPositionMarker = window.L.marker(userPosition, {icon})
      this.map.addLayer(this.userPositionMarker)
    }

    // user position moved
    else if (userPosition) {
      this.userPositionMarker.setLatLng(userPosition)
    }
  }

  setLocationMarker (props) {
    if (!this.map) {
      setTimeout(() => {
        this.setLocationMarker(props)
      }, 50)
      return
    }
    // don't remove marker if the position was available before
    const locationMarkerPosition = /*this.userPositionLatLng || */props.locationMarkerPosition
    console.log('set location marker', locationMarkerPosition)

    console.log('set location marker 2', this.locationMarker, props.locationMarkerPosition, props.config, props.config.mapCenterMarker)

    // add marker if there wasn't one available before
    if (!this.locationMarker && props.locationMarkerPosition && props.config && props.config.mapCenterMarker) {
      console.log('set location marker 3', locationMarkerPosition)

      const icon = window.L.icon({
        iconUrl: props.config.mapCenterMarker,

        iconSize: [40, 40], // size of the icon
        iconAnchor: [20, 40], // point of the icon which will correspond to marker's location
        popupAnchor: [0, -42] // point from which the popup should open relative to the iconAnchor
      })

      this.locationMarker = window.L.marker(locationMarkerPosition, {icon})
      this.map.addLayer(this.locationMarker)
    }
  }

  /**
   * @param poi {Poi}
   */
  showPopup = (poi) => {
    // console.log('show popup')
    if (poi) {
      this.disablePopupClose = false
      this.map.closePopup()
      const latLng = {lat: poi.lat, lng: poi.lng}
      this.panTo(latLng)
      this.addPanEndCallback(() => {
        const marker = this.currentMarkers[poi.id]

        const openPopup = () => {
          this.disabledPanningForPoisWithLatLng = [latLng]
          if (marker && !marker.isPopupOpen()) {
            marker.openPopup()
          }
        }

        const parent = marker.__parent
        const visibleParent1 = this.markerClusterGroup.getVisibleParent(marker)

        if (parent && parent.zoomToBounds && parent._childClusters.length === 0 && visibleParent1.__parent !== parent) {
          parent.zoomToBounds()
          setTimeout(() => {
            const visibleParent2 = this.markerClusterGroup.getVisibleParent(marker)
            if (parent._childClusters.length === 0 && visibleParent2.__parent !== parent) {
              parent.spiderfy()
              setTimeout(() => {
                openPopup()
              }, 500)
            }
            else {
              openPopup()
            }
          }, 1000)
        }
        else {
          openPopup()
        }

        // const visibleParent = this.markerClusterGroup.getVisibleParent(marker)
        // if (visibleParent && visibleParent.spiderfy) {
        //   this.disabledPanningForPoisWithLatLng = [latLng]
        //   this.markerClusterGroup.once('animationend', openPopup)
        //   visibleParent.spiderfy()
        // }
        // else {
        //   openPopup()
        // }

      })
    }
  }

  unloadScriptsAndStyles () {
    const ids = Object.keys(SCRIPT_URLS).map(key => this.getScriptId(key))
      .concat(Object.keys(CSS_URLS).map(key => this.getCssId(key)))
      .concat(this.getCssId(EXTRA_CSS_KEY))
    ids.forEach(id => {
      const child = document.getElementById(id)
      if (child && child.parentNode) {
        child.parentNode.removeChild(child)
      }
    })
  }

  render () {
    return (
      <div
        id={this.outerDivId}
        style={{width: '100%', height: '100%'}}
      />
    )
  }

}

Leaflet.propTypes = {

  provider: PropTypes.oneOf(Object.values(PROVIDERS)).isRequired,  // choose your tile provider

  /**
   * HERE maps settings
   */
  appId: PropTypes.string,
  appCode: PropTypes.string,
  useCIT: PropTypes.bool,        // develop server -> true; production server -> false
  lang: PropTypes.string,

  /**
   * OSM settings
   */
  // -> none

  /**
   * This leaflet component registers itself with this function at the caller
   */
  controllerInterfaceRegistration: PropTypes.func,

  /**
   * General map settings
   */
  initialBounds: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number
    })
  ),

  pois: PropTypes.array,

  showUserPosition: PropTypes.bool,
  initiallyGoToUserPosition: PropTypes.bool,

  requestExtraData: PropTypes.func,
  onBubbleTapped: PropTypes.func,

  onBoundsChanged: PropTypes.func,

  renderPoiBubbleComponent: PropTypes.func,
  config: PropTypes.object,
  functions: PropTypes.object.isRequired,
  dataSource: PropTypes.string,

  defaultUserPosition: PropTypes.object

}

Leaflet.defaultProps = {
  /**
   * HERE maps settings
   */
  lang: 'en_US',

  /**
   * Controller callback
   */
  controllerInterfaceRegistration: null,

  /**
   * General map settings
   */
  initialBounds: BOUNDS_EUROPE,

  pois: [],

  showUserPosition: true,
  initiallyGoToUserPosition: true,

  // requestExtraData: null,
  onBubbleTapped: null,
  onBoundsChanged: null,

  dataSource: '',

  locationMarkerPosition: null
}

export default Leaflet



// WEBPACK FOOTER //
// ./src/components/Leaflet/Leaflet.js